<template>
  <div class="post-card">
    <!-- Corner brackets -->
    <div class="corner tl"></div>
    <div class="corner br"></div>

    <!-- ── HEADER ── -->
    <div class="post-hd">
      <div class="post-user">
        <div class="avatar" @click="goToProfile">
          <img v-if="post.userImageURL" :src="post.userImageURL" :alt="post.fullName" />
          <span v-else>{{ (post.fullName || '?').charAt(0) }}</span>
        </div>
        <div>
          <p class="post-author" @click="goToProfile">{{ post.fullName || 'Anonymous' }}</p>
          <p class="post-time">{{ post.timeAgo }}</p>
        </div>
      </div>

      <!-- Owner menu -->
      <div v-if="post.uid === currentUserUid" class="menu-wrap">
        <button class="menu-btn" @click="menuOpen = !menuOpen">
          <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="5" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="12" cy="19" r="1.2"/></svg>
        </button>
        <div v-if="menuOpen" class="menu-dropdown">
          <button class="menu-item" @click="editPost">Edit Recommendation</button>
          <button class="menu-item menu-item-danger" @click="deletePost">Delete Recommendation</button>
        </div>
      </div>

      <!-- Non-owner menu: report -->
      <div v-else class="menu-wrap">
        <button class="menu-btn" @click="menuOpen = !menuOpen">
          <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="5" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="12" cy="19" r="1.2"/></svg>
        </button>
        <div v-if="menuOpen" class="menu-dropdown">
          <button v-if="!reportSubmitted" class="menu-item menu-item-danger" @click="reportMenuOpen = true; menuOpen = false">Report Post</button>
          <span v-else class="menu-item menu-item-disabled">Reported ✓</span>
        </div>
      </div>

      <!-- Report reason picker -->
      <v-dialog v-model="reportMenuOpen" max-width="360">
        <div class="report-panel">
          <p class="report-title">Report this post</p>
          <button v-for="reason in reportReasons" :key="reason" class="report-reason-btn" @click="submitReport(reason)">
            {{ reason }}
          </button>
          <button class="report-cancel-btn" @click="reportMenuOpen = false">Cancel</button>
        </div>
      </v-dialog>
    </div>

    <div class="post-divider"></div>

    <!-- ── PLACE HEADER ── -->
    <div class="place-hd">
      <div class="place-tags">
        <span class="place-tag place-tag-city">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
          {{ post.city || 'Unknown City' }}
        </span>
        <span class="place-tag place-tag-cat">{{ post.category || 'Recommendation' }}</span>
      </div>
      <p class="place-name">{{ post.businessName || 'Untitled Place' }}</p>
      <div v-if="post.wantsArrangement" class="arrangement-flag">
        <div class="arrangement-flag-dot"></div>
        <span>Member requested an official concierge arrangement</span>
      </div>
    </div>

    <!-- ── BODY ── -->
    <div class="post-body">
      <p v-if="post.text" class="post-text">{{ post.text }}</p>
      <div v-if="mediaItems.length" class="media-gallery" :class="{ single: mediaItems.length === 1 }">
        <div v-for="(item, idx) in mediaItems" :key="idx" class="media-gallery-item">
          <img
            v-if="item.type === 'image'"
            :src="item.url"
            alt="Post image"
            @click="openImageViewer(item.url)"
          />
          <video v-else controls :src="item.url"></video>
        </div>
      </div>
    </div>

    <!-- ── ARRANGEMENT INTEREST ROW ── -->
    <div v-if="post.arrangementCount > 0" class="liked-row">
      <button class="liked-label" @click="openInterestedUsersPanel">
        {{ post.arrangementCount }} {{ post.arrangementCount === 1 ? 'member wants' : 'members want' }} this arrangement &nbsp;·&nbsp; view who
      </button>
    </div>

    <div class="post-divider"></div>

    <!-- ── ACTIONS ── -->
    <div class="post-footer">
      <button
        class="action-btn arrangement-btn"
        :class="{ liked: post.hasRequestedArrangement }"
        @click="$emit('request-arrangement', post.id)"
      >
        <svg viewBox="0 0 24 24" :fill="post.hasRequestedArrangement ? 'rgba(210,175,100,0.3)' : 'none'" stroke="currentColor" stroke-width="1.5">
          <path d="M12 2l2.9 6.26L21 9.27l-4.5 4.38L17.8 20 12 16.9 6.2 20l1.3-6.35L3 9.27l6.1-1.01L12 2z"/>
        </svg>
        <span>{{ post.hasRequestedArrangement ? 'I want this too' : 'I want this too' }}</span>
      </button>
      <button class="action-btn" @click="showComments = true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
        <span>{{ post.comments || 0 }}</span>
      </button>
    </div>

    <!-- ── COMMENT SHEET ── -->
    <CommentSheet
      :postID="post.id"
      collection-name="explore"
      :show="showComments"
      @close="showComments = false"
    />

    <!-- ── IMAGE VIEWER OVERLAY ── -->
    <!-- Was a hand-rolled <Teleport to="body"><div style="position:fixed">,
         which is exactly the class of bug that keeps recurring across
         browsers/devices (ancestor transforms, WebKit stale-paint, etc).
         v-dialog's fullscreen mode already solves viewport positioning
         correctly — it manages its own overlay container appended to
         <body> and sizes off the real viewport — so ride that instead of
         re-solving positioning by hand. Matches the working pattern in
         School Connect's ExplorePostCard.vue. -->
    <v-dialog v-model="imageDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <div class="tin-media-lightbox" @click.self="imageDialog = false">
        <img :src="selectedImage" class="tin-media-lightbox-img" alt="Full size" />
        <div class="tin-media-lightbox-actions">
          <button class="tin-media-lightbox-btn" @click="downloadImage(selectedImage)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </button>
          <button class="tin-media-lightbox-btn" @click="imageDialog = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- ── INTERESTED MEMBERS PANEL ── -->
    <v-dialog v-model="likedUsersDialog" max-width="420">
      <div class="panel">
        <div class="panel-hd">
          <div class="section-hd">
            <div class="section-rule l"></div>
            <span class="section-eyebrow">Demand Signal</span>
            <div class="section-rule"></div>
          </div>
          <h3 class="panel-title">Members Who <em>Want This</em></h3>
        </div>

        <div v-if="likedUsers.length === 0" class="panel-empty">
          <p class="empty-sub">No requests yet</p>
        </div>
        <div v-else class="liked-users-list">
          <div v-for="user in likedUsers" :key="user.uid" class="liked-user">
            <div class="liked-avatar">
              <img v-if="user.image" :src="user.image" :alt="user.fullName" />
              <span v-else>{{ (user.fullName || '?').charAt(0) }}</span>
            </div>
            <span class="liked-name">{{ user.fullName }}</span>
          </div>
        </div>

        <button class="panel-close" @click="likedUsersDialog = false">
          <div class="btn-tick"></div>
          <span>Close</span>
          <div class="btn-tick"></div>
        </button>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import CommentSheet from '@/components/CommentSheet.vue'
import { db } from '@/firebase'
import { getDoc, doc, getDocs, collection, query, where } from 'firebase/firestore'

export default {
  name: 'ExplorePostCard',
  components: { CommentSheet },

  props: {
    post: { type: Object, required: true },
    currentUserUid: { type: String, default: null },
  },

  emits: ['request-arrangement', 'comment-post', 'delete-post', 'edit-post'],

  data() {
    return {
      showComments: false,
      menuOpen: false,
      reportMenuOpen: false,
      reportSubmitted: false,
      reportReasons: ['Spam', 'Inappropriate content', 'Harassment', 'False information', 'Other'],
      imageDialog: false,
      selectedImage: '',
      likedUsersDialog: false,
      likedUsers: [],
    }
  },

  computed: {
    // New posts store a `media` array; older posts only have single imageURL/videoURL fields.
    mediaItems() {
      if (Array.isArray(this.post.media) && this.post.media.length) return this.post.media
      const legacy = []
      if (this.post.imageURL) legacy.push({ url: this.post.imageURL, type: 'image' })
      if (this.post.videoURL) legacy.push({ url: this.post.videoURL, type: 'video' })
      return legacy
    },
  },

  methods: {
    goToProfile() {
      if (this.post.uid) {
        this.$router.push({ name: 'MemberDetail', params: { id: this.post.uid } })
      }
    },

    // Mobile Safari has a longstanding bug where a `position: fixed` element
    // inserted into the DOM (e.g. via v-if) while the page is not actively
    // scrolling gets painted at a stale layout position instead of the true
    // viewport, and only corrects itself on the next scroll event. That's
    // exactly why the lightbox appeared to require scrolling to "find" it,
    // even with the overlay correctly Teleported to <body>. That's now moot:
    // the lightbox uses v-dialog instead of a hand-rolled fixed div, and
    // Vuetify's own overlay system positions correctly against the real
    // viewport without needing this kind of workaround.

    openImageViewer(url) {
      this.selectedImage = url
      this.imageDialog = true
    },

    downloadImage(url) {
      const link = document.createElement('a')
      link.href = url
      link.download = 'image.jpg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    editPost() {
      this.menuOpen = false
      this.$emit('edit-post', this.post)
    },

    deletePost() {
      this.menuOpen = false
      this.$emit('delete-post', this.post.id)
    },

    submitReport(reason) {
      this.reportMenuOpen = false
      this.reportSubmitted = true
      this.$emit('report-post', { post: this.post, reason })
    },

    async openInterestedUsersPanel() {
      try {
        const docSnap = await getDoc(doc(db, 'explore', this.post.id))
        const interestedUIDs = docSnap.exists() ? docSnap.data().arrangementInterest || [] : []
        if (!Array.isArray(interestedUIDs) || interestedUIDs.length === 0) {
          this.likedUsers = []
          this.likedUsersDialog = true
          return
        }
        const users = []
        await Promise.all(
          interestedUIDs.map(async uid => {
            const q = query(collection(db, 'user'), where('uid', '==', uid))
            const snap = await getDocs(q)
            if (!snap.empty) {
              const d = snap.docs[0].data()
              users.push({ uid, fullName: d.fullName || 'Unknown', image: d.image || '' })
            }
          })
        )
        this.likedUsers = users
        this.likedUsersDialog = true
      } catch (error) {
        console.error('Error fetching interested members:', error)
      }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

/* ── CARD ── */
.post-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); position: relative; font-family: 'Montserrat', sans-serif; }
.corner { position: absolute; width: 12px; height: 12px; z-index: 1; pointer-events: none; }
.corner.tl { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.corner.br { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }

/* ── HEADER ── */
.post-hd { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; }
.post-user { display: flex; align-items: center; gap: 12px; }
.avatar { width: 38px; height: 38px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 16px; font-weight: 300; color: rgba(210,175,100,0.5); flex-shrink: 0; cursor: pointer; transition: border-color 0.2s; }
.avatar:hover { border-color: rgba(210,175,100,0.5); }
.avatar img { width: 100%; height: 100%; object-fit: cover; }
.post-author { font-family: 'Cormorant', serif; font-size: 18px; font-weight: 400; color: #edeae2; line-height: 1.1; margin-bottom: 2px; cursor: pointer; transition: color 0.2s; }
.post-author:hover { color: rgba(210,175,100,0.8); }
.post-time { font-size: 9px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.2); }

/* ── MENU ── */
.menu-wrap { position: relative; }
.menu-btn { background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.2); padding: 4px; display: flex; align-items: center; transition: color 0.2s; }
.menu-btn:hover { color: rgba(210,175,100,0.6); }
.menu-btn svg { width: 16px; height: 16px; }
.menu-dropdown { position: absolute; top: calc(100% + 6px); right: 0; background: #0f0f10; border: 1px solid rgba(210,175,100,0.2); min-width: 160px; z-index: 50; }
.menu-item { display: block; width: 100%; padding: 12px 16px; background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.4); text-align: left; transition: color 0.2s, background 0.2s; }
.menu-item:hover { background: rgba(210,175,100,0.05); color: rgba(237,232,223,0.8); }
.menu-item-danger { color: rgba(180,70,70,0.5); }
.menu-item-danger:hover { color: rgba(200,80,80,0.9); background: rgba(200,80,80,0.04); }
.menu-item-disabled { display: block; padding: 10px 14px; font-size: 12.5px; color: rgba(130,190,140,0.6); }

/* ── REPORT PANEL ── */
/* v-dialog now supplies the backdrop, centering and stacking context. */
.report-panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.2); width: 100%; max-width: 340px; padding: 24px; }
.report-title { font-family: 'Cormorant', serif; font-size: 19px; color: #edeae2; margin-bottom: 16px; }
.report-reason-btn { display: block; width: 100%; text-align: left; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.1); color: rgba(237,232,223,0.6); font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 300; padding: 12px 14px; margin-bottom: 8px; cursor: pointer; }
.report-reason-btn:hover { border-color: rgba(200,80,80,0.35); color: rgba(200,80,80,0.85); }
.report-cancel-btn { display: block; width: 100%; text-align: center; background: none; border: none; color: rgba(237,232,223,0.3); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; padding: 10px; margin-top: 6px; cursor: pointer; }

/* ── DIVIDER ── */
.post-divider { height: 1px; background: rgba(210,175,100,0.07); }

/* ── PLACE HEADER ── */
.place-hd { padding: 16px 20px 6px; }
.place-tags { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.place-tag { display: inline-flex; align-items: center; gap: 5px; font-size: 8.5px; font-weight: 600; letter-spacing: 1.8px; text-transform: uppercase; padding: 5px 10px; border: 1px solid rgba(210,175,100,0.25); color: rgba(210,175,100,0.7); }
.place-tag svg { width: 11px; height: 11px; }
.place-tag-city { color: rgba(210,175,100,0.75); border-color: rgba(210,175,100,0.3); }
.place-tag-cat { background: rgba(210,175,100,0.08); }
.place-name { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 400; color: #edeae2; line-height: 1.15; margin-bottom: 10px; }
.arrangement-flag { display: flex; align-items: center; gap: 8px; font-size: 9.5px; font-weight: 500; letter-spacing: 0.6px; color: rgba(210,175,100,0.55); margin-bottom: 10px; }
.arrangement-flag-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(210,175,100,0.7); box-shadow: 0 0 6px rgba(210,175,100,0.4); flex-shrink: 0; }

/* ── BODY ── */
.post-body { padding: 4px 20px 18px; }
.post-text { font-size: 14px; font-weight: 300; color: rgba(237,232,223,0.65); line-height: 1.8; letter-spacing: 0.2px; margin-bottom: 14px; }
.post-text:last-child { margin-bottom: 0; }
.media-gallery { display: grid; grid-template-columns: repeat(2, 1fr); gap: 3px; margin-top: 4px; }
.media-gallery.single { grid-template-columns: 1fr; }
.media-gallery-item { position: relative; aspect-ratio: 1; overflow: hidden; }
.media-gallery.single .media-gallery-item { aspect-ratio: 16/10; }
.media-gallery-item img, .media-gallery-item video { width: 100%; height: 100%; object-fit: cover; display: block; filter: brightness(0.88) saturate(0.75); cursor: pointer; }
.media-gallery-item video { cursor: default; }

/* ── ARRANGEMENT INTEREST ROW ── */
.liked-row { padding: 10px 20px; }
.liked-label { background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(210,175,100,0.35); transition: color 0.2s; padding: 0; }
.liked-label:hover { color: rgba(210,175,100,0.7); }

/* ── FOOTER ── */
.post-footer { display: flex; }
.action-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px 0; background: transparent; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); transition: color 0.2s; }
.action-btn:hover { color: rgba(210,175,100,0.6); }
.action-btn + .action-btn { border-left: 1px solid rgba(210,175,100,0.07); }
.action-btn svg { width: 15px; height: 15px; }
.action-btn.liked { color: rgba(210,175,100,0.8); }
.action-btn.liked svg { stroke: rgba(210,175,100,0.9); }

/* ── IMAGE OVERLAY ── */
/* v-dialog's fullscreen mode already fills and positions against the true
   viewport — this just needs to fill that space and center its contents,
   not re-establish its own position:fixed. */
.tin-media-lightbox { width: 100%; min-height: 100vh; background: rgba(0,0,0,0.88); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 20px; padding: 24px; }
.tin-media-lightbox-img { max-width: 90vw; max-height: 80vh; object-fit: contain; }
.tin-media-lightbox-actions { display: flex; gap: 16px; }
.tin-media-lightbox-btn { width: 44px; height: 44px; border: 1px solid rgba(210,175,100,0.3); background: transparent; color: rgba(210,175,100,0.6); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: border-color 0.2s, color 0.2s; }
.tin-media-lightbox-btn:hover { border-color: rgba(210,175,100,0.7); color: rgba(210,175,100,1); }
.tin-media-lightbox-btn svg { width: 18px; height: 18px; }

/* ── INTERESTED MEMBERS PANEL ── */
/* v-dialog now supplies the backdrop, centering and stacking context. */
.panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.15); width: 100%; max-width: 420px; padding: 32px; position: relative; }
.panel::before, .panel::after { content: ''; position: absolute; width: 14px; height: 14px; }
.panel::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.panel::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.panel-hd { margin-bottom: 24px; }
.section-hd { display: flex; align-items: center; gap: 16px; margin-bottom: 10px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.panel-title { font-family: 'Cormorant', serif; font-size: 28px; font-weight: 300; color: #edeae2; }
.panel-title em { font-style: italic; color: rgba(210,175,100,0.8); }
.panel-empty { padding: 24px 0; text-align: center; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; }
.liked-users-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 28px; max-height: 320px; overflow-y: auto; }
.liked-user { display: flex; align-items: center; gap: 12px; }
.liked-avatar { width: 36px; height: 36px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 15px; color: rgba(210,175,100,0.5); flex-shrink: 0; }
.liked-avatar img { width: 100%; height: 100%; object-fit: cover; }
.liked-name { font-family: 'Cormorant', serif; font-size: 17px; font-weight: 400; color: rgba(237,232,223,0.7); }
.panel-close { width: 100%; border: 1px solid rgba(210,175,100,0.2); background: transparent; padding: 14px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(210,175,100,0.5); display: flex; align-items: center; justify-content: center; gap: 14px; transition: border-color 0.2s, color 0.2s; }
.panel-close:hover { border-color: rgba(210,175,100,0.5); color: rgba(210,175,100,0.9); }
.btn-tick { width: 16px; height: 1px; background: rgba(210,175,100,0.3); flex-shrink: 0; }
</style>